/** Tow reports: a vehicle a deputy had towed or impounded, the owner notice, police holds, and release. */
import { useEffect, useState } from 'react';
import { RACES, TOW_DAMAGE_SPOTS, TOW_HOLD_REASONS, TOW_REASON_LABEL, TOW_REASONS, TOW_STATUS_LABEL, US_STATES, VEHICLE_COLORS, type TowReason } from '@civilos/domain';
import { SignaturePad } from '../shared/affidavit.tsx';
import { LicenseScanButton } from '../shared/LicenseScan.tsx';
import { DamageDiagram } from './PaperPrints.tsx';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, KPI, when } from './common.tsx';

const carName = (v: any) => [v?.year, v?.color, v?.make, v?.model].filter(Boolean).join(' ') || 'Vehicle';
const TONE: Record<string, 'ok' | 'warn' | 'bad' | 'neutral' | 'info'> = { stored: 'warn', released: 'ok', disposed: 'neutral' };

export function TowsPage() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<'mine' | 'lot' | 'all'>('mine');
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const r = useLoad(() => api.get<any>(`/api/tows?q=${encodeURIComponent(query)}&mine=${tab === 'mine' ? 1 : 0}&status=${tab === 'lot' ? 'stored' : ''}`), [query, tab]);
  const c = r.data?.counts;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Records"
        title="Tow reports"
        sub="Vehicles towed or impounded: where they are, the owner notice, holds and release."
        actions={
          <button className="btn primary" onClick={() => navigate(at('/tows/new'))}>
            New tow report
          </button>
        }
      />
      {c && (
        <div className="ops-kpis">
          <KPI n={c.stored} label="At the tow lot" tone={c.stored ? 'warn' : undefined} />
          <KPI n={c.unnotified} label="Owner not notified" tone={c.unnotified ? 'bad' : undefined} />
          <KPI n={c.hold} label="On a police hold" />
        </div>
      )}
      <div className="row between wrap gap">
        <div className="seg-ctl" role="tablist" aria-label="Which tows">
          {(r.data?.canSeeAll ? (['mine', 'lot', 'all'] as const) : (['mine'] as const)).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
              {{ mine: 'My tows', lot: 'At the lot', all: 'All' }[k]}
            </button>
          ))}
        </div>
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tag, VIN, tow #, owner, place…" aria-label="Search tows" />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.tows.length ? (
        <EmptyState icon="car" title="No tow reports">
          {tab === 'mine' ? 'Tows you write show up here.' : undefined}
        </EmptyState>
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Tow #</th>
                <th>Towed</th>
                <th>Vehicle</th>
                <th>Tag</th>
                <th>Reason</th>
                <th>Tow company</th>
                <th>Deputy</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.tows.map((t: any) => (
                <tr key={t.id} className="clickable" onClick={() => navigate(at(`/tows/${t.id}`))}>
                  <td className="mono">{t.number}</td>
                  <td>{when(t.towed_at)}</td>
                  <td>{carName(t.vehicle)}</td>
                  <td className="mono">{t.plate ?? '—'}</td>
                  <td>{TOW_REASON_LABEL[t.reason as TowReason]}</td>
                  <td>{t.tow_company}</td>
                  <td>{t.deputy_name}</td>
                  <td>
                    <Badge tone={TONE[t.status]} dot>
                      {TOW_STATUS_LABEL[t.status as keyof typeof TOW_STATUS_LABEL]}
                    </Badge>{' '}
                    {t.status === 'stored' && t.hold && !t.hold_cleared_at && <Badge tone="bad">Hold</Badge>}{' '}
                    {t.status === 'stored' && !t.owner_notified_at && <Badge tone="warn">Owner not notified</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export function NewTow() {
  const { navigate } = useRouter();
  const [f, setF] = useState<Record<string, any>>({ reason: 'arrest', towedAt: nowLocal(), location: '', towCompany: '', towDriver: '', storedAt: '', inventory: '', damage: '', keysWith: '', caseNumber: '', notes: '', holdReason: '' });
  const [v, setV] = useState<Record<string, string>>({ year: '', make: '', model: '', style: '', color: '', plate: '', plateState: 'SC', regYear: '', vin: '' });
  const [o, setO] = useState<Record<string, string>>({ last: '', first: '', middle: '', address: '', phone: '' });
  const [op, setOp] = useState<Record<string, any>>({ sameAsOwner: false, last: '', first: '', middle: '', sex: '', race: '', dob: '', dl: '', dlState: 'SC', address: '', phone: '' });
  const [x, setX] = useState<Record<string, any>>({ releaseContact: '', ownerNotNotifiedReason: '', towPhone: '', towAddress: '', towCity: '', storageAddress: '', storageCity: '', keysInVehicle: null, locked: null, trunkLocked: null, damageSpots: [] as number[] });
  const sop = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setOp({ ...op, [k]: e.target.value });
  const sx = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setX({ ...x, [k]: e.target.value });
  const [hold, setHold] = useState(false);
  const a = useAction();
  useEffect(() => setHold(TOW_HOLD_REASONS.includes(f.reason)), [f.reason]);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const sv = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV({ ...v, [k]: e.target.value });
  const so = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setO({ ...o, [k]: e.target.value });
  const here = () =>
    navigator.geolocation?.getCurrentPosition(
      (p) => setF((x) => ({ ...x, lat: p.coords.latitude, lng: p.coords.longitude, location: x.location || `GPS ${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)}` })),
      () => undefined,
      { enableHighAccuracy: true, timeout: 8000 },
    );
  return (
    <div className="ops stack">
      <PageHeader crumb="Tow reports" title="New tow report" />
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const r = await api.post<{ id: string }>('/api/tows', {
              ...f,
              towedAt: new Date(f.towedAt).toISOString(),
              vehicle: { ...v, regYear: v.regYear || undefined, year: v.year || undefined },
              owner: o,
              operator: op.sameAsOwner ? { ...o, sameAsOwner: true, sex: op.sex, race: op.race, dob: op.dob || undefined, dl: op.dl, dlState: op.dlState } : { ...op, dob: op.dob || undefined },
              extra: x,
              hold,
              holdReason: hold ? f.holdReason || undefined : undefined,
            });
            navigate(at(`/tows/${r.id}`), true);
          });
        }}
      >
        <Card title="The tow">
          <div className="grid-3">
            <label>
              Reason
              <select value={f.reason} onChange={set('reason')}>
                {TOW_REASONS.map((k) => (
                  <option key={k} value={k}>
                    {TOW_REASON_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Towed at
              <input type="datetime-local" value={f.towedAt} onChange={set('towedAt')} required />
            </label>
            <label>
              Case # (if there’s a report)
              <input value={f.caseNumber} onChange={set('caseNumber')} placeholder="2026-000123" />
            </label>
          </div>
          <label>
            Where it was towed from
            <span className="row gap-sm">
              <input value={f.location} onChange={set('location')} required placeholder="Address, road and mile marker, or business" style={{ flex: 1 }} />
              <button type="button" className="btn" onClick={here}>
                Use my location
              </button>
            </span>
          </label>
        </Card>
        <Card title="Vehicle">
          <div className="grid-4">
            <label>
              Year
              <input value={v.year} onChange={sv('year')} inputMode="numeric" maxLength={4} />
            </label>
            <label>
              Make
              <input value={v.make} onChange={sv('make')} required />
            </label>
            <label>
              Model
              <input value={v.model} onChange={sv('model')} />
            </label>
            <label>
              Color
              <select value={v.color} onChange={sv('color')}>
                <option value="">—</option>
                {VEHICLE_COLORS.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
            <label>
              Tag
              <input value={v.plate} onChange={sv('plate')} className="mono" />
            </label>
            <label>
              Tag state
              <select value={v.plateState} onChange={sv('plateState')}>
                {US_STATES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Tag year
              <input value={v.regYear} onChange={sv('regYear')} inputMode="numeric" maxLength={4} />
            </label>
            <label>
              Style
              <input value={v.style} onChange={sv('style')} placeholder="4D, PK, SUV…" />
            </label>
            <label style={{ gridColumn: 'span 2' }}>
              VIN
              <input value={v.vin} onChange={sv('vin')} className="mono" maxLength={17} />
            </label>
          </div>
          <p className="small muted">Enter the tag or the VIN (both if you have them).</p>
        </Card>
        <Card title="Registered owner (if known)" actions={<LicenseScanButton small onScan={(d) => setO({ ...o, last: d.last ?? '', first: d.first ?? '', middle: d.middle ?? '', address: d.address ?? '' })} />}>
          <div className="grid-3">
            <label>
              Last name
              <input value={o.last} onChange={so('last')} />
            </label>
            <label>
              First
              <input value={o.first} onChange={so('first')} />
            </label>
            <label>
              Middle
              <input value={o.middle} onChange={so('middle')} />
            </label>
            <label>
              Address
              <input value={o.address} onChange={so('address')} />
            </label>
            <label>
              Phone
              <input value={o.phone} onChange={so('phone')} inputMode="tel" />
            </label>
          </div>
          <label>
            If the owner can’t be notified, why
            <input value={x.ownerNotNotifiedReason} onChange={sx('ownerNotNotifiedReason')} placeholder="Leave blank if you’ll notify them" />
          </label>
        </Card>
        <Card title="Vehicle operator (driver)" actions={<LicenseScanButton small onScan={(d) => setOp({ ...op, sameAsOwner: false, last: d.last ?? '', first: d.first ?? '', middle: d.middle ?? '', sex: d.sex ?? '', dob: d.dob ?? '', dl: d.dl ?? '', dlState: d.dlState ?? 'SC', address: d.address ?? '' })} />}>
          <label className="check">
            <input type="checkbox" checked={op.sameAsOwner} onChange={(e) => setOp({ ...op, sameAsOwner: e.target.checked })} /> The owner was driving
          </label>
          <div className="grid-4">
            {!op.sameAsOwner && (
              <>
                <label>
                  Last name
                  <input value={op.last} onChange={sop('last')} />
                </label>
                <label>
                  First
                  <input value={op.first} onChange={sop('first')} />
                </label>
                <label>
                  Middle
                  <input value={op.middle} onChange={sop('middle')} />
                </label>
              </>
            )}
            <label>
              Sex
              <select value={op.sex} onChange={sop('sex')}>
                <option value="">—</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </label>
            <label>
              Race
              <select value={op.race} onChange={sop('race')}>
                <option value="">—</option>
                {RACES.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Date of birth
              <input type="date" value={op.dob} onChange={sop('dob')} />
            </label>
            <label>
              Operator’s license #
              <input value={op.dl} onChange={sop('dl')} className="mono" />
            </label>
            <label>
              State
              <select value={op.dlState} onChange={sop('dlState')}>
                {US_STATES.map((st) => (
                  <option key={st}>{st}</option>
                ))}
              </select>
            </label>
            {!op.sameAsOwner && (
              <>
                <label style={{ gridColumn: 'span 2' }}>
                  Address
                  <input value={op.address} onChange={sop('address')} />
                </label>
                <label>
                  Phone
                  <input value={op.phone} onChange={sop('phone')} inputMode="tel" />
                </label>
              </>
            )}
          </div>
        </Card>
        <Card title="Tow company and storage">
          <div className="grid-3">
            <label>
              Tow company
              <input value={f.towCompany} onChange={set('towCompany')} required />
            </label>
            <label>
              Driver
              <input value={f.towDriver} onChange={set('towDriver')} />
            </label>
            <label>
              Tow company phone
              <input value={x.towPhone} onChange={sx('towPhone')} inputMode="tel" />
            </label>
            <label>
              Tow company address
              <input value={x.towAddress} onChange={sx('towAddress')} />
            </label>
            <label>
              City / state
              <input value={x.towCity} onChange={sx('towCity')} />
            </label>
            <label>
              Stored at
              <input value={f.storedAt} onChange={set('storedAt')} placeholder="Lot name" />
            </label>
            <label>
              Lot address
              <input value={x.storageAddress} onChange={sx('storageAddress')} />
            </label>
            <label>
              City / state
              <input value={x.storageCity} onChange={sx('storageCity')} />
            </label>
            <label>
              Keys are with
              <input value={f.keysWith} onChange={set('keysWith')} placeholder="With the vehicle, with tow driver, none" />
            </label>
            <label>
              Contact for release
              <input value={x.releaseContact} onChange={sx('releaseContact')} placeholder="Who the owner calls to get it back" />
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={hold} onChange={(e) => setHold(e.target.checked)} /> Police hold: don’t release until a supervisor clears it
          </label>
          {hold && !TOW_HOLD_REASONS.includes(f.reason) && (
            <label>
              Why the hold
              <input value={f.holdReason} onChange={set('holdReason')} required />
            </label>
          )}
        </Card>
        <Card title="Inventory and damage">
          <div className="tw-yn">
            {(
              [
                ['keysInVehicle', 'Keys in vehicle'],
                ['locked', 'Vehicle locked'],
                ['trunkLocked', 'Trunk locked'],
              ] as const
            ).map(([k, l]) => (
              <div key={k} className="row gap-sm" role="radiogroup" aria-label={l}>
                <span className="strong small">{l}</span>
                <label className="check">
                  <input type="radio" name={k} checked={x[k] === true} onChange={() => setX({ ...x, [k]: true })} /> Yes
                </label>
                <label className="check">
                  <input type="radio" name={k} checked={x[k] === false} onChange={() => setX({ ...x, [k]: false })} /> No
                </label>
              </div>
            ))}
          </div>
          <div className="tw-dmg">
            <DamageDiagram spots={x.damageSpots} onToggle={(n) => setX({ ...x, damageSpots: x.damageSpots.includes(n) ? x.damageSpots.filter((y: number) => y !== n) : [...x.damageSpots, n] })} />
            <div className="small">
              <strong>Body damage:</strong> tap where the car is damaged.
              <div className="muted">{x.damageSpots.length ? TOW_DAMAGE_SPOTS.filter((sp) => x.damageSpots.includes(sp.n)).map((sp) => `${sp.n} ${sp.label}`).join(', ') : 'None marked'}</div>
            </div>
          </div>
          <label>
            Inventory of the vehicle
            <textarea rows={3} value={f.inventory} onChange={set('inventory')} placeholder="Items of value in the vehicle and trunk" />
          </label>
          <label>
            Existing damage
            <textarea rows={2} value={f.damage} onChange={set('damage')} />
          </label>
          <label>
            Notes
            <textarea rows={2} value={f.notes} onChange={set('notes')} />
          </label>
        </Card>
        <ErrorBox error={a.error} />
        <div className="row gap">
          <button className="btn primary" disabled={a.busy}>
            {a.busy ? 'Saving…' : 'Save tow report'}
          </button>
          <button type="button" className="btn ghost" onClick={() => history.back()}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export function TowDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/tows/${id}`), [id]);
  const a = useAction();
  const [sig, setSig] = useState<string | null>(null);
  const [sigName, setSigName] = useState('');
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { tow: t, events, can } = r.data;
  const act = (body: Record<string, unknown>) => a.run(async () => (await api.post(`/api/tows/${id}/act`, body), r.reload()));
  const holdOn = t.hold && !t.hold_cleared_at;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Tow reports"
        title={`${t.number} · ${carName(t.vehicle)}`}
        sub={`${TOW_REASON_LABEL[t.reason as TowReason]} · towed ${when(t.towed_at)} by ${t.tow_company} · ${t.deputy_name}`}
        actions={
          <div className="row gap-sm">
            <Badge tone={TONE[t.status]} dot>
              {TOW_STATUS_LABEL[t.status as keyof typeof TOW_STATUS_LABEL]}
            </Badge>
            <button className="btn" onClick={() => navigate(at(`/tows/${id}/print`))}>
              Print tow report
            </button>
          </div>
        }
      />
      {r.data.ncic?.length > 0 && (
        <Notice tone="bad">
          This vehicle is still in NCIC ({r.data.ncic.map((n: any) => `${n.number}${n.nic_number ? ` · NIC ${n.nic_number}` : ''}`).join('; ')}).{' '}
          <button className="btn link" onClick={() => navigate(at(`/forms/${r.data.ncic[0].id}`))}>
            Request the deletion
          </button>
        </Notice>
      )}
      {holdOn && <Notice tone="bad">Police hold: {t.hold_reason}. The vehicle can’t be released until a supervisor clears the hold.</Notice>}
      {t.status === 'stored' && !t.owner_notified_at && <Notice tone="warn">The owner hasn’t been notified yet.</Notice>}
      <ErrorBox error={a.error} />
      <div className="grid-2-cards">
        <Card title="Vehicle">
          <dl className="kv">
            <dt>Vehicle</dt>
            <dd>{carName(t.vehicle)}</dd>
            <dt>Tag</dt>
            <dd className="mono">{t.plate ? `${t.plate} ${t.vehicle?.plateState ?? ''}` : '—'}</dd>
            <dt>VIN</dt>
            <dd className="mono">{t.vin ?? '—'}</dd>
            <dt>Owner</dt>
            <dd>{[t.owner?.name, t.owner?.address, t.owner?.phone].filter(Boolean).join(' · ') || '—'}</dd>
            <dt>Driver</dt>
            <dd>{t.operator?.sameAsOwner ? 'The owner' : [t.operator?.name, t.operator?.dl && `DL ${t.operator.dl} ${t.operator.dlState ?? ''}`, t.operator?.address].filter(Boolean).join(' · ') || '—'}</dd>
            <dt>Keys</dt>
            <dd>
              {[t.keys_with, t.extra?.keysInVehicle === true ? 'in the vehicle' : t.extra?.keysInVehicle === false ? 'not in the vehicle' : null, t.extra?.locked === true ? 'locked' : t.extra?.locked === false ? 'unlocked' : null, t.extra?.trunkLocked === true ? 'trunk locked' : null]
                .filter(Boolean)
                .join(' · ') || '—'}
            </dd>
          </dl>
        </Card>
        <Card title="Tow">
          <dl className="kv">
            <dt>From</dt>
            <dd>{t.location}</dd>
            <dt>Tow company</dt>
            <dd>
              {t.tow_company}
              {t.tow_driver ? ` · ${t.tow_driver}` : ''}
            </dd>
            <dt>Stored at</dt>
            <dd>{t.stored_at ?? '—'}</dd>
            <dt>Case #</dt>
            <dd>{t.case_number ?? '—'}</dd>
            <dt>Owner notified</dt>
            <dd>{t.owner_notified_at ? `${when(t.owner_notified_at)} · ${t.owner_notified_how}` : 'Not yet'}</dd>
            {t.released_at && (
              <>
                <dt>{t.status === 'disposed' ? 'Disposed' : 'Released'}</dt>
                <dd>
                  {when(t.released_at)}
                  {t.released_to ? ` to ${t.released_to}` : ''}
                  {t.release_note ? ` — ${t.release_note}` : ''}
                </dd>
              </>
            )}
          </dl>
        </Card>
      </div>
      {(t.inventory || t.damage || t.notes || t.extra?.damageSpots?.length > 0) && (
        <Card title="Inventory and notes">
          {t.extra?.damageSpots?.length > 0 && (
            <p>
              <strong>Body damage:</strong> {TOW_DAMAGE_SPOTS.filter((sp) => t.extra.damageSpots.includes(sp.n)).map((sp) => `${sp.n} ${sp.label}`).join(', ')}
            </p>
          )}
          {t.inventory && (
            <p>
              <strong>Inventory:</strong> {t.inventory}
            </p>
          )}
          {t.damage && (
            <p>
              <strong>Damage:</strong> {t.damage}
            </p>
          )}
          {t.notes && <p>{t.notes}</p>}
        </Card>
      )}
      {(can.driverSign || r.data.driverSignature) && (
        <Card title="Tow truck driver signs the inventory">
          <p className="small">“We, the undersigned, hereby certify the above listed information is correct and the inventory contains all articles within.”</p>
          {r.data.driverSignature ? (
            <div className="row gap-sm">
              <img src={r.data.driverSignature} alt="Tow truck driver signature" className="fm-sigimg" style={{ width: 240, height: 70 }} />
              <span className="small">
                {t.driver_signed_name} · {when(t.driver_signed_at)}
              </span>
              {can.driverSign && (
                <button className="btn small ghost" onClick={() => a.run(async () => (await api.post(`/api/tows/${id}/driver-sign`, { image: null }), r.reload()))}>
                  Redo
                </button>
              )}
            </div>
          ) : (
            <div className="stack sm">
              <label>
                Driver’s name
                <input value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder={t.tow_driver ?? ''} />
              </label>
              <SignaturePad onChange={setSig} />
              <button className="btn primary" disabled={!sig || a.busy} onClick={() => a.run(async () => (await api.post(`/api/tows/${id}/driver-sign`, { image: sig, name: sigName || undefined }), r.reload()))}>
                Save the driver’s signature
              </button>
            </div>
          )}
        </Card>
      )}
      {t.status === 'stored' && can.edit && <FeesCard t={t} onDone={r.reload} />}
      {t.status === 'stored' && (
        <Card title="Next steps">
          <div className="row gap wrap">
            {can.notify && !t.owner_notified_at && (
              <button
                className="btn primary"
                disabled={a.busy}
                onClick={() => {
                  const how = window.prompt('How was the owner notified? (phone call, certified letter, in person…)')?.trim();
                  if (how) act({ action: 'notify_owner', how });
                }}
              >
                Owner notified
              </button>
            )}
            {can.clearHold && (
              <button className="btn" disabled={a.busy} onClick={() => act({ action: 'clear_hold', note: window.prompt('Note (optional)')?.trim() || undefined })}>
                Clear the hold
              </button>
            )}
            {can.release && (
              <button
                className="btn"
                disabled={a.busy}
                onClick={() => {
                  const releasedTo = window.prompt('Released to (name, and how ownership was checked):')?.trim();
                  if (releasedTo) act({ action: 'release', releasedTo });
                }}
              >
                Release the vehicle
              </button>
            )}
            {can.dispose && (
              <button
                className="btn ghost"
                disabled={a.busy}
                onClick={() => {
                  const note = window.prompt('How was it disposed of? (sold at auction, scrapped, notice dates)')?.trim();
                  if (note) act({ action: 'dispose', note });
                }}
              >
                Mark disposed
              </button>
            )}
            <button
              className="btn ghost"
              disabled={a.busy}
              onClick={() => {
                const note = window.prompt('Add a note')?.trim();
                if (note) act({ action: 'note', note });
              }}
            >
              Add a note
            </button>
          </div>
        </Card>
      )}
      <Card title="History">
        <ul className="timeline">
          {events.map((e: any, i: number) => (
            <li key={i}>
              <strong>{e.action.replace(/_/g, ' ')}</strong> <span className="muted small">{when(e.at)} · {e.by_name}</span>
              {e.note && <div className="small">{e.note}</div>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function FeesCard({ t, onDone }: { t: any; onDone: () => void }) {
  const [w, setW] = useState(t.extra?.wreckerFee ?? '');
  const [s, setS] = useState(t.extra?.storageFee ?? '');
  const a = useAction();
  return (
    <Card title="Fees">
      <form
        className="row gap-sm wrap"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => (await api.post(`/api/tows/${t.id}`, { extra: { ...(t.extra ?? {}), wreckerFee: w === '' ? null : Number(w), storageFee: s === '' ? null : Number(s) } }), onDone()));
        }}
      >
        <label>
          Wrecker fee ($)
          <input value={w} onChange={(e) => setW(e.target.value)} inputMode="decimal" />
        </label>
        <label>
          Storage fee ($)
          <input value={s} onChange={(e) => setS(e.target.value)} inputMode="decimal" />
        </label>
        <button className="btn" disabled={a.busy}>
          Save fees
        </button>
      </form>
      <ErrorBox error={a.error} />
    </Card>
  );
}
